import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { Truck, Edit, Trash2, CalendarIcon, Plus } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import type { EquipmentEntry } from "@/domain/rdo/schema";

type EquipmentsProps = {
  value: EquipmentEntry[];
  onChange: (v: EquipmentEntry[]) => void;
  mode: "create" | "edit" | "view";
};

export function EquipmentsModule({ value, onChange, mode }: EquipmentsProps) {
  const [editingEquipment, setEditingEquipment] = useState<EquipmentEntry | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEquipment, setNewEquipment] = useState<EquipmentEntry>({
    id: "",
    name: "",
    qty: 1,
    rented: false,
    rentDueDate: undefined,
    notes: "",
  });
  const [showRentDatePicker, setShowRentDatePicker] = useState(false);
  const [showEditRentDatePicker, setShowEditRentDatePicker] = useState(false);
  const { toast } = useToast();

  const handleEdit = (equipment: EquipmentEntry, index: number) => {
    setEditingEquipment({ ...equipment });
    setEditingIndex(index);
  };

  const handleSaveEdit = () => {
    if (!editingEquipment || editingIndex === null) return;

    if (editingEquipment.qty < 0) {
      toast({
        title: "Erro de validação",
        description: "A quantidade deve ser maior ou igual a 0",
        variant: "destructive",
      });
      return;
    }

    if (editingEquipment.rented && !editingEquipment.rentDueDate) {
      toast({
        title: "Erro de validação",
        description: "Data de vencimento é obrigatória para equipamentos locados",
        variant: "destructive",
      });
      return;
    }

    const updatedEquipments = [...value];
    updatedEquipments[editingIndex] = editingEquipment;
    onChange(updatedEquipments);
    
    setEditingEquipment(null);
    setEditingIndex(null);
    
    toast({
      title: "Equipamento atualizado",
      description: "As alterações foram salvas com sucesso",
    });
  };

  const handleCancelEdit = () => {
    setEditingEquipment(null);
    setEditingIndex(null);
  };

  const handleDelete = (index: number) => {
    const updatedEquipments = value.filter((_, i) => i !== index);
    onChange(updatedEquipments);
    setDeleteConfirmIndex(null);
    
    toast({
      title: "Equipamento removido",
      description: "O equipamento foi removido com sucesso",
    });
  };

  const handleAddEquipment = () => {
    if (!newEquipment.name.trim()) {
      toast({
        title: "Erro de validação",
        description: "Nome do equipamento é obrigatório",
        variant: "destructive",
      });
      return;
    }

    if (newEquipment.qty < 0) {
      toast({
        title: "Erro de validação",
        description: "A quantidade deve ser maior ou igual a 0",
        variant: "destructive",
      });
      return;
    }

    if (newEquipment.rented && !newEquipment.rentDueDate) {
      toast({
        title: "Erro de validação",
        description: "Data de vencimento é obrigatória para equipamentos locados",
        variant: "destructive",
      });
      return;
    }

    const equipmentToAdd: EquipmentEntry = {
      ...newEquipment,
      id: crypto.randomUUID(),
    };

    onChange([...value, equipmentToAdd]);
    
    // Reset form
    setNewEquipment({
      id: "",
      name: "",
      qty: 1,
      rented: false,
      rentDueDate: undefined,
      notes: "",
    });
    setShowAddModal(false);
    
    toast({
      title: "Equipamento adicionado",
      description: "O equipamento foi adicionado com sucesso",
    });
  };

  const formatRentDate = (date?: string) => {
    if (!date) return "Não definida";
    try {
      return format(new Date(date), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return "Data inválida";
    }
  };

  return (
    <>
      <Card data-section="equipments">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Truck className="w-5 h-5" />
            Equipamentos ({value.length})
            {mode !== "view" && (
              <Button 
                size="sm" 
                className="ml-2"
                onClick={() => setShowAddModal(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Adicionar
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {value.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Truck className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum equipamento adicionado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {value.map((equipment, index) => (
                <div
                  key={equipment.id || index}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{equipment.name}</p>
                      {equipment.rented && (
                        <Badge variant="secondary" className="text-xs">
                          Locado
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>Quantidade: {equipment.qty}</span>
                      {equipment.rented && equipment.rentDueDate && (
                        <>
                          <span>•</span>
                          <span>Vence em: {formatRentDate(equipment.rentDueDate)}</span>
                        </>
                      )}
                    </div>
                    {equipment.notes && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {equipment.notes}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {mode !== "view" && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(equipment, index)}
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteConfirmIndex(index)}
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Equipment Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Adicionar Equipamento</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="equipment-name">Nome do Equipamento</Label>
              <Input
                id="equipment-name"
                value={newEquipment.name}
                onChange={(e) => setNewEquipment({ ...newEquipment, name: e.target.value })}
                placeholder="Ex: Escavadeira, Betoneira..."
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="equipment-qty">Quantidade</Label>
              <Input
                id="equipment-qty"
                type="number"
                min="0"
                value={newEquipment.qty}
                onChange={(e) => setNewEquipment({ ...newEquipment, qty: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="equipment-rented"
                checked={newEquipment.rented}
                onCheckedChange={(rented) => setNewEquipment({ ...newEquipment, rented, rentDueDate: rented ? newEquipment.rentDueDate : undefined })}
              />
              <Label htmlFor="equipment-rented">Equipamento Locado</Label>
            </div>
            {newEquipment.rented && (
              <div className="grid gap-2">
                <Label>Data de Vencimento da Locação</Label>
                <Popover open={showRentDatePicker} onOpenChange={setShowRentDatePicker}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "justify-start text-left font-normal",
                        !newEquipment.rentDueDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {newEquipment.rentDueDate ? format(new Date(newEquipment.rentDueDate), "dd/MM/yyyy", { locale: ptBR }) : "Selecionar data"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={newEquipment.rentDueDate ? new Date(newEquipment.rentDueDate) : undefined}
                      onSelect={(date) => {
                        setNewEquipment({ ...newEquipment, rentDueDate: date?.toISOString().split('T')[0] });
                        setShowRentDatePicker(false);
                      }}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}
            <div className="grid gap-2">
              <Label htmlFor="equipment-notes">Observações (Opcional)</Label>
              <Textarea
                id="equipment-notes"
                value={newEquipment.notes}
                onChange={(e) => setNewEquipment({ ...newEquipment, notes: e.target.value })}
                placeholder="Observações sobre o equipamento..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddEquipment}>
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Equipment Modal */}
      {editingEquipment && (
        <Dialog open={!!editingEquipment} onOpenChange={() => handleCancelEdit()}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Editar Equipamento</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Nome do Equipamento</Label>
                <Input
                  value={editingEquipment.name}
                  readOnly
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">O nome não pode ser alterado</p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-equipment-qty">Quantidade</Label>
                <Input
                  id="edit-equipment-qty"
                  type="number"
                  min="0"
                  value={editingEquipment.qty}
                  onChange={(e) => setEditingEquipment({ ...editingEquipment, qty: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-equipment-rented"
                  checked={editingEquipment.rented}
                  onCheckedChange={(rented) => setEditingEquipment({ ...editingEquipment, rented, rentDueDate: rented ? editingEquipment.rentDueDate : undefined })}
                />
                <Label htmlFor="edit-equipment-rented">Equipamento Locado</Label>
              </div>
              {editingEquipment.rented && (
                <div className="grid gap-2">
                  <Label>Data de Vencimento da Locação</Label>
                  <Popover open={showEditRentDatePicker} onOpenChange={setShowEditRentDatePicker}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "justify-start text-left font-normal",
                          !editingEquipment.rentDueDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {editingEquipment.rentDueDate ? format(new Date(editingEquipment.rentDueDate), "dd/MM/yyyy", { locale: ptBR }) : "Selecionar data"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={editingEquipment.rentDueDate ? new Date(editingEquipment.rentDueDate) : undefined}
                        onSelect={(date) => {
                          setEditingEquipment({ ...editingEquipment, rentDueDate: date?.toISOString().split('T')[0] });
                          setShowEditRentDatePicker(false);
                        }}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}
              <div className="grid gap-2">
                <Label htmlFor="edit-equipment-notes">Observações</Label>
                <Textarea
                  id="edit-equipment-notes"
                  value={editingEquipment.notes || ""}
                  onChange={(e) => setEditingEquipment({ ...editingEquipment, notes: e.target.value })}
                  placeholder="Observações sobre o equipamento..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCancelEdit}>
                Cancelar
              </Button>
              <Button onClick={handleSaveEdit}>
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog 
        open={deleteConfirmIndex !== null} 
        onOpenChange={() => setDeleteConfirmIndex(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este equipamento? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => deleteConfirmIndex !== null && handleDelete(deleteConfirmIndex)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}